import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { TrendingUp, TrendingDown, DollarSign, ArrowRightLeft, Calculator } from 'lucide-react';
import { useDetectorKeywordsReportQuery } from '@/lib/query-options';
import { Spinner } from '@/components/ui/spinner';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';

interface BudgetAdjustment {
  campaign_id: number;
  campaign_name: string;
  adjustment_type: 'increase' | 'decrease';
  reason: string;
  current_brand_percentage: number;
  new_brand_percentage: number;
  current_daily_budget: number;
  new_brand_daily_budget: number;
  new_prospecting_daily_budget: number;
  impression_loss: number;
  spend_utilization: number;
  total_daily_budget: number;
  prospecting_campaigns: Array<{ id: number; name: string }>;
}

interface BudgetDetectorData {
  id: number;
  name: string;
  budget_adjustments: BudgetAdjustment[];
  analysis_summary: {
    total_adjustments: number;
    analysis_type: string;
    message: string;
  };
}

export const BudgetDetectorDashboard = ({
  campaignId,
  detector,
  detectorLabel,
}: {
  campaignId?: number;
  detector?: string;
  detectorLabel?: string;
}) => {
  const [customBrandBudget, setCustomBrandBudget] = useState<string>('');
  const [showCustomCalculation, setShowCustomCalculation] = useState(false);
  
  const { data: reportData, isLoading, error } = useDetectorKeywordsReportQuery(
    campaignId || 0,
    detector || ''
  ) as { data: BudgetDetectorData | undefined; isLoading: boolean; error: Error | null };
  
  const { symbol: currencySymbol } = useCurrencySymbol('google');

  if (isLoading) {
    return (
      <Card className="p-6 bg-white border">
        <div className="flex items-center justify-center p-8">
          <Spinner />
          <span className="ml-2">Analyzing budget allocation...</span>
        </div>
      </Card>
    );
  }

  if (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
    return (
      <Card className="p-6 bg-white border">
        <Alert className="border-red-200 bg-red-50">
          <AlertDescription>
            <strong>Error analyzing budget:</strong> {errorMessage}
          </AlertDescription>
        </Alert>
      </Card>
    );
  }

  if (!reportData || !reportData.budget_adjustments?.length) {
    return (
      <Card className="p-6 bg-white border">
        <div className="space-y-4">
          <CardHeader className="p-0">
            <CardTitle className="text-lg font-medium">{detectorLabel}</CardTitle>
          </CardHeader>
          <Alert className="border-green-200 bg-green-50">
            <AlertDescription>
              <strong>No Budget Adjustments Needed:</strong> Current budget allocation is optimal based on performance data.
            </AlertDescription>
          </Alert>
        </div>
      </Card>
    );
  }

  const budgetAdjustment = reportData.budget_adjustments[0]; // Take first adjustment
  const totalAccountBudget = budgetAdjustment.total_daily_budget * 30; // Convert to monthly

  // Calculate custom brand budget allocation if user provides input
  const calculateCustomAllocation = () => {
    const brandBudgetValue = parseFloat(customBrandBudget);
    if (isNaN(brandBudgetValue) || brandBudgetValue <= 0) return null;
    
    const remainingBudget = totalAccountBudget - brandBudgetValue;
    return {
      brandBudget: brandBudgetValue,
      remainingBudget: Math.max(0, remainingBudget),
      brandPercentage: (brandBudgetValue / totalAccountBudget) * 100,
      remainingPercentage: (remainingBudget / totalAccountBudget) * 100
    };
  };

  const customAllocation = calculateCustomAllocation();

  return (
    <Card className="p-6 bg-white border">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <CardHeader className="p-0">
            <CardTitle className="text-lg font-medium">{detectorLabel}</CardTitle>
          </CardHeader>
          <Badge variant={budgetAdjustment.adjustment_type === 'increase' ? 'default' : 'secondary'}>
            {budgetAdjustment.adjustment_type === 'increase' ? (
              <>
                <TrendingUp className="w-3 h-3 mr-1" />
                Increase Recommended
              </>
            ) : (
              <>
                <TrendingDown className="w-3 h-3 mr-1" />
                Decrease Recommended
              </>
            )}
          </Badge>
        </div>

        <CardContent className="p-0 space-y-6">
          {/* Analysis Reason */}
          <div className="p-4 bg-blue-50 border-l-4 border-blue-400 rounded-r-lg">
            <div className="text-sm font-medium text-blue-800 mb-1">Analysis Result</div>
            <div className="text-sm text-blue-700">{budgetAdjustment.reason}</div>
          </div>

          {/* Current Performance Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <DollarSign className="h-4 w-4 text-gray-700" />
                <span className="text-sm font-medium text-gray-700">Performance Metrics</span>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm">Impression Loss:</span>
                  <span className={`font-medium ${budgetAdjustment.impression_loss > 0.1 ? 'text-red-600' : 'text-green-600'}`}>
                    {(budgetAdjustment.impression_loss * 100).toFixed(1)}%
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">Spend Utilization:</span>
                  <span className={`font-medium ${budgetAdjustment.spend_utilization < 0.8 ? 'text-yellow-600' : 'text-green-600'}`}>
                    {(budgetAdjustment.spend_utilization * 100).toFixed(1)}%
                  </span>
                </div>
              </div>
            </div>

            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <Calculator className="h-4 w-4 text-gray-700" />
                <span className="text-sm font-medium text-gray-700">Account Budget</span>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm">Total Monthly Budget:</span>
                  <span className="font-medium">{currencySymbol}{totalAccountBudget.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">Daily Budget:</span>
                  <span className="font-medium">{currencySymbol}{budgetAdjustment.total_daily_budget.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Budget Allocation Comparison */}
          <div className="p-4 border-2 border-blue-100 bg-blue-50 rounded-lg">
            <h3 className="text-lg font-semibold text-blue-900 mb-4">Recommended Budget Allocation</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Current Allocation */}
              <div className="text-center">
                <div className="text-sm text-gray-700 mb-2">Current Brand Budget</div>
                <div className="text-xl font-bold text-gray-900">
                  {currencySymbol}{(budgetAdjustment.current_daily_budget * 30).toFixed(2)}
                </div>
                <div className="text-sm text-gray-500">
                  {budgetAdjustment.current_brand_percentage.toFixed(1)}% of total
                </div>
              </div>

              <div className="flex items-center justify-center">
                <ArrowRightLeft className="h-6 w-6 text-blue-600" />
              </div>

              {/* Recommended Allocation */}
              <div className="text-center">
                <div className="text-sm text-blue-600 mb-2">Recommended Brand Budget</div>
                <div className="text-xl font-bold text-blue-600">
                  {currencySymbol}{(budgetAdjustment.new_brand_daily_budget * 30).toFixed(2)}
                </div>
                <div className="text-sm text-blue-500">
                  {budgetAdjustment.new_brand_percentage.toFixed(1)}% of total
                </div>
              </div>
            </div>

            {/* Remaining Budget for Other Campaigns */}
            <div className="mt-4 p-3 bg-white rounded border">
              <div className="text-sm font-medium text-gray-700 mb-2">Remaining Budget for Other Campaigns:</div>
              <div className="text-lg font-semibold text-green-700">
                {currencySymbol}{(budgetAdjustment.new_prospecting_daily_budget * 30).toFixed(2)}
              </div>
              {budgetAdjustment.prospecting_campaigns.length > 0 && (
                <div className="text-xs text-gray-500 mt-1">
                  Distributed across {budgetAdjustment.prospecting_campaigns.length} campaigns: {' '}
                  {budgetAdjustment.prospecting_campaigns.map(c => c.name).join(', ')}
                </div>
              )}
            </div>
          </div>

          {/* Custom Brand Budget Calculator */}
          <div className="border-2 border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Custom Brand Budget Calculator</h3>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setShowCustomCalculation(!showCustomCalculation)}
              >
                {showCustomCalculation ? 'Hide' : 'Show'} Calculator
              </Button>
            </div>
            
            {showCustomCalculation && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="brand-budget">Brand Budget Amount</Label>
                    <Input
                      id="brand-budget"
                      type="number"
                      placeholder="Enter brand budget..."
                      value={customBrandBudget}
                      onChange={(e) => setCustomBrandBudget(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div className="flex items-end">
                    <div className="text-sm text-gray-700">
                      Total Account Budget: {currencySymbol}{totalAccountBudget.toFixed(2)}
                    </div>
                  </div>
                </div>

                {customAllocation && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-green-50 rounded-lg">
                    <div>
                      <div className="text-sm font-medium text-green-800">Brand Budget</div>
                      <div className="text-lg font-bold text-green-900">
                        {currencySymbol}{customAllocation.brandBudget.toFixed(2)}
                      </div>
                      <div className="text-sm text-green-600">
                        {customAllocation.brandPercentage.toFixed(1)}% of total
                      </div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-green-800">Remaining Budget</div>
                      <div className="text-lg font-bold text-green-900">
                        {currencySymbol}{customAllocation.remainingBudget.toFixed(2)}
                      </div>
                      <div className="text-sm text-green-600">
                        {customAllocation.remainingPercentage.toFixed(1)}% of total
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Action Summary */}
          <Alert className={budgetAdjustment.adjustment_type === 'increase' ? 'border-blue-200 bg-blue-50' : 'border-yellow-200 bg-yellow-50'}>
            <AlertDescription>
              <strong>Recommendation Summary:</strong> {budgetAdjustment.adjustment_type === 'increase' ? 'Increase' : 'Decrease'} brand budget 
              from {currencySymbol}{(budgetAdjustment.current_daily_budget * 30).toFixed(2)} to {currencySymbol}{(budgetAdjustment.new_brand_daily_budget * 30).toFixed(2)} 
              ({(budgetAdjustment.new_brand_percentage - budgetAdjustment.current_brand_percentage > 0 ? '+' : '')}{(budgetAdjustment.new_brand_percentage - budgetAdjustment.current_brand_percentage).toFixed(1)} percentage points)
            </AlertDescription>
          </Alert>
        </CardContent>
      </div>
    </Card>
  );
};